import React from "react";
import styled from "styled-components";
import { colors } from "../../utils/theme";
import RevealOnScroll from "../../utils/RevealOnScroll";
import WorkButtons from "../Header/WorkButtons";
import { SiFigma, SiAdobephotoshop, SiAdobeillustrator, SiReact, SiCss3 } from "react-icons/si";
import { motion } from "framer-motion";
import TextShift from "../../utils/TextShift";
import { Link } from "react-router-dom";
import socialNestGif from "../../images/FeaturedProjects/SocialNest/SocialGif.gif";
import jamaisVuGif from "../../images/FeaturedProjects/JamaisVu/JamaisVu.gif";
import transcendanceGif from "../../images/FeaturedProjects/Transcendance/Tran.gif";

const DesignContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-height: 100vh;
  color: white;
  background: transparent;
  padding: 0 1rem 4rem;
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 0.5rem 3rem;
  }
`;

const TitleContainer = styled.div`
  margin-top: 6rem;
  margin-bottom: 0;
  opacity: 0.9;
  width: 100%;
  display: flex;
  justify-content: center;
  @media (max-width: 768px) {
    margin-top: 1rem;
    padding-bottom: 1.3rem;
  }
`;

const NavigationContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin: 0 auto;
  margin-top: -2rem;
`;

const SubtextContainer = styled.div`
  width: 100%;
  max-width: 800px;
  text-align: left;
  padding: 2rem;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 0.5rem;
    max-width: 100%;
  }
`;

const Subtext = styled.p`
  font-family: "maitree", serif;
  font-size: 1.4rem;
  line-height: 1.6;
  color: #9f9f9f;
  font-weight: 300;
  max-width: 600px;
  margin: 0;
  text-align: left;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    width: 350px;
    padding: 0;
  }
`;

const TechStackContainer = styled.div`
  width: 100%;
  max-width: 800px;
  padding: 1rem 2rem 0;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 6.4rem;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 1rem 0rem 0;
    gap: 3rem;
    justify-content: center;
    flex-wrap: wrap;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    gap: 3rem;
    padding: 1rem 0 0;
  }
`;

const IconWrapper = styled(motion.div)`
  color: #9f9f9f;
  font-size: 2.4rem;
  opacity: 0.7;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
  }

  &:hover {
    color: white;
    opacity: 1;
    transform: translateY(-2px);
  }
`;

const iconVariants = {
  initial: { y: 20, opacity: 0 },
  animate: { y: 0, opacity: 1 },
};

const ProjectsContainer = styled.div`
  width: 100%;
  max-width: 900px;
  padding: 4rem 2rem 6rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin: 0 auto;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    max-width: 100%;
    padding: 4rem 1.5rem 5rem;
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 350px;
    padding: 2rem 0 4rem;
    gap: 1.5rem;
  }
`;

const ProjectBox = styled(Link)`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0;
  background: rgba(50, 50, 50, 0.5);
  border-radius: 10px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  text-decoration: none;
  color: white;
  position: relative;
  overflow: hidden;
  height: 400px;
  width: 100%;
  transition: all 0.3s ease-in-out;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    height: 350px;
  }

  @media (max-width: 768px) {
    height: 300px;
    width: 100%;
  }

  @media (max-width: 480px) {
    height: 250px;
  }

  &:hover {
    border-color: rgba(255, 255, 255, 0.3);
    background: rgba(50, 50, 50, 0.7);
    .project-gif {
      filter: blur(0);
      opacity: 0.7;
    }
  }
`;

const ProjectGif = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  filter: blur(5px);
  opacity: 0.3;
  transition: all 0.3s ease-in-out;
  z-index: 0;
`;

const ProjectInfo = styled.div`
  position: relative;
  z-index: 1;
  padding: 2rem;
  width: 100%;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const ProjectTitle = styled.h3`
  font-size: 2rem;
  margin: 0;
  font-weight: 500;
  color: ${colors.titleColor};
  font-family: "moret-variable", serif;
`;

const ProjectDescription = styled.p`
  font-size: 1.1rem;
  color: ${colors.subtitleColor};
  line-height: 1.6;
  margin: 1rem 0;
  font-family: "maitree", serif;
`;

const Tag = styled.span`
  font-size: 1rem;
  color: ${colors.subtitleColor};
  font-weight: 400;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const TagContainer = styled.div`
  display: flex;
  gap: 0.6rem;
  margin-top: 0.6rem;
`;

function Design() {
  const featuredProjects = [
    {
      title: "Social Nest",
      description: "A website to explore and visualize social connections.",
      tags: ["UI/UX", "Full-stack", "Figma"],
      link: "/SocialNest",
      gif: socialNestGif
    },
    {
      title: "Jamais-Vu",
      description: "An immersive installation exploring soundscapes of Montreal.",
      tags: ["Installation", "Touchdesigner", "Soundscapes", "Live"],
      link: "/JamaisVu",
      gif: jamaisVuGif
    },
    {
      title: "Transcendance",
      description: "A live installation exploring collective identity.",
      tags: ["Installation", "Touchdesigner", "Live"],
      link: "/transcendence",
      gif: transcendanceGif
    }
  ];

  const [playingStates, setPlayingStates] = React.useState(
    featuredProjects.reduce((acc, _, index) => ({ ...acc, [index]: false }), {})
  );

  const handleMouseEnter = (index) => {
    setPlayingStates(prev => ({ ...prev, [index]: true }));
  };

  const handleMouseLeave = (index) => {
    setPlayingStates(prev => ({ ...prev, [index]: false }));
  };

  return (
    <DesignContainer>
      <RevealOnScroll delay={0}>
        <TitleContainer>
          <TextShift
            text="Design"
            fontSize="10rem"
            mobileFontSize="5rem"
            fontFamily="moret-variable, serif"
            maxWeight={900}
            minWeight={200}
            adjacentWeightChange={100}
          />
        </TitleContainer>
      </RevealOnScroll>
      <RevealOnScroll delay={200}>
        <NavigationContainer>
          <WorkButtons excludeButton="design" />
        </NavigationContainer>
      </RevealOnScroll>
      <RevealOnScroll delay={300}>
        <SubtextContainer>
          <Subtext>
          I design immersive, interactive experiences that connect viewers to the work. I challenge myself to learn new skills and push technical boundaries, blending precision with storytelling to create impactful projects.          </Subtext>
        </SubtextContainer>
      </RevealOnScroll>
      <RevealOnScroll delay={400}>
        <TechStackContainer>
          {[
            { Icon: SiFigma, delay: 0 },
            { Icon: SiAdobephotoshop, delay: 100 },
            { Icon: SiAdobeillustrator, delay: 200 },
            { Icon: SiReact, delay: 300 },
            { Icon: SiCss3, delay: 400 }
          ].map(({ Icon, delay }, index) => (
            <IconWrapper
              key={index}
              variants={iconVariants}
              initial="initial"
              animate="animate"
              transition={{ duration: 0.5, delay: delay / 1000 }}
            >
              <Icon />
            </IconWrapper>
          ))}
        </TechStackContainer>
      </RevealOnScroll>
      <ProjectsContainer>
        {featuredProjects.map((project, index) => (
          <RevealOnScroll delay={400}>
            <ProjectBox 
              to={project.link}
              onMouseEnter={() => handleMouseEnter(index)}
              onMouseLeave={() => handleMouseLeave(index)}
            >
              <ProjectGif 
                className="project-gif"
                data-playing={playingStates[index]}
                style={{
                  backgroundImage: `url(${project.gif})`,
                  animationPlayState: playingStates[index] ? 'running' : 'paused'
                }}
              />
              <ProjectInfo>
                <div>
                  <ProjectTitle>{project.title}</ProjectTitle>
                  <ProjectDescription>{project.description}</ProjectDescription>
                  <TagContainer>
                    {project.tags.map((tag, tagIndex) => (
                      <React.Fragment key={tagIndex}>
                        <Tag>{tag}</Tag>
                        {tagIndex < project.tags.length - 1 && <Tag>•</Tag>}
                      </React.Fragment>
                    ))}
                  </TagContainer>
                </div>
              </ProjectInfo>
            </ProjectBox>
          </RevealOnScroll>
        ))}
      </ProjectsContainer>
    </DesignContainer>
  );
}

export default Design;
